import { useEffect, useMemo } from 'react';
import PaystackPop from '@paystack/inline-js';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createPaystackOrder } from '../api/orderAPI';
import { useValidateFullOrDownPayment } from './useValidateFullOrDownPayment';
import {
  setDownPaymentAmount,
  setDownPaymentSuccess,
  setPaystackOrderReference,
} from '../features/order/fullPaymentSlice';
import { clearCart } from '../features/cart/cartSlice';
import { setMandateData } from '../features/paystack/mandateSlice';
import { useOrders } from '../pages/userDashboard/shopping/shoppingOverviewContents/purchase/useOrders';
import {
  selectDeliveryAddress,
  selectDeliveryType,
} from '../features/order/deliveryAddressSlice';

export function useDownPayment() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { refetchOrders } = useOrders();

  const { data: user } = useSelector((state) => state.user);
  const latestAddress = user?.latest_address;

  const mandateData = useSelector((state) => state.mandate.data);
  const paystackOrderReference = useSelector(
    (state) => state.fullPayment.paystackOrderReference
  );
  const deliveryAddressData = useSelector(selectDeliveryAddress);
  const deliveryType = useSelector(selectDeliveryType);
  const cart = useSelector((state) => state.cart.cart);

  // --- Calculate totals ---
  const totalCartPrice = useMemo(
    () => cart.reduce((acc, item) => acc + item.price * item.quantity, 0),
    [cart]
  );

  const VAT = useMemo(() => 0.075 * totalCartPrice, [totalCartPrice]);
  const shippingFee = useMemo(() => deliveryType?.amount || 0, [deliveryType]);
  const total = useMemo(
    () => totalCartPrice + VAT + shippingFee,
    [totalCartPrice, VAT, shippingFee]
  );

  // --- Delivery address string ---
  const deliveryAddress = [
    deliveryAddressData?.streetAddress || latestAddress?.streetAddress,
    deliveryAddressData?.state || latestAddress?.state,
  ]
    .filter(Boolean)
    .join(', ');

  // --- Validate payment ---
  const {
    data: validationData,
    refetch: refetchValidation,
    isLoading: isValidatingPayment,
  } = useValidateFullOrDownPayment(paystackOrderReference);

  useEffect(() => {
    const { payment_verified, status } = validationData || {};
    const success = payment_verified && status === 'success';
    dispatch(setDownPaymentSuccess(success));
  }, [validationData, dispatch]);

  // --- Paystack transaction mutation ---
  const { mutate: payForDownPayment, isPending: isValidating } = useMutation({
    mutationFn: () => createPaystackOrder(mandateData),
    onSuccess: (res) => {
      const config = res.data?.paystack_config;
      if (!config) return;

      const { reference, email, key, amount } = config;
      dispatch(setDownPaymentAmount(amount));

      if (!reference) return;

      const paystack = new PaystackPop();
      paystack.newTransaction({
        key,
        email,
        amount: amount * 100,
        currency: 'NGN',
        reference,
        onSuccess: (transaction) => {
          const isApproved =
            transaction.status === 'success' &&
            transaction.message === 'Approved';

          if (isApproved) {
            dispatch(setPaystackOrderReference(transaction.reference));
            dispatch(setMandateData(null));
            dispatch(clearCart());
            refetchOrders();
          } else {
            dispatch(setPaystackOrderReference(null));
          }

          refetchValidation();
        },
        onError: () => {
          dispatch(setPaystackOrderReference(null));
        },
      });
    },
  });

  const handlePayDownPayment = (downPayment) => {
    if (!downPayment) return;
    payForDownPayment(mandateData);
  };

  return {
    handlePayDownPayment,
    isValidating: isValidating || isValidatingPayment,
    validationData,
    deliveryAddress,
    total,
  };
}
